import { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { signInWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../services/firebase';

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [showReset, setShowReset] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const clubParam = searchParams.get('club');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);

    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      
      // Check user role and redirect accordingly
      const userDoc = await getDoc(doc(db, 'users', userCredential.user.uid));
      const clubSuffix = clubParam ? `?club=${clubParam}` : '';
      
      if (userDoc.exists()) {
        const role = userDoc.data().role;
        const userClubId = userDoc.data().clubId;
        // Use user's clubId if no club param provided
        const finalClubSuffix = clubParam ? `?club=${clubParam}` : (userClubId ? `?club=${userClubId}` : '');
        
        if (role === 'club_admin' || role === 'super_admin' || role === 'admin') {
          navigate(`/admin/dashboard${finalClubSuffix}`);
        } else if (role === 'coach') {
          navigate(`/coach${finalClubSuffix}`); // Coaches go to coach dashboard
        } else {
          navigate(`/dashboard${finalClubSuffix}`); // Players go to player dashboard
        }
      } else {
        navigate(`/dashboard${clubSuffix}`); // New users go to player dashboard
      }
    } catch (err) {
      console.error('Login error:', err);
      if (err.code === 'auth/user-not-found' || err.code === 'auth/wrong-password') {
        setError('Invalid email or password');
      } else if (err.code === 'auth/invalid-email') {
        setError('Invalid email format');
      } else if (err.code === 'auth/invalid-credential') {
        setError('Invalid email or password');
      } else {
        setError('Failed to sign in. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    if (!email) {
      setError('Please enter your email address first');
      return;
    }
    setError('');
    setLoading(true);

    try {
      await sendPasswordResetEmail(auth, email);
      setMessage('Password reset email sent! Check your inbox.');
      setShowReset(false);
    } catch (err) {
      console.error('Reset error:', err);
      if (err.code === 'auth/user-not-found') {
        setError('No account found with this email');
      } else {
        setError('Failed to send reset email. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-header">
          <span className="login-logo">⚽</span>
          <h1>Welcome Back</h1>
          <p>Sign in to ClubLink TT</p>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          {error && <div className="login-error">{error}</div>}
          {message && <div className="login-success">{message}</div>}
          
          <div className="login-form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              required
            />
          </div>

          <div className="login-form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              required
            />
          </div>

          <button 
            type="submit" 
            className="login-btn"
            disabled={loading}
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        <div className="login-links">
          {showReset ? (
            <div className="reset-section">
              <p>Enter your email above, then:</p>
              <button 
                onClick={handlePasswordReset} 
                className="reset-btn"
                disabled={loading}
              >
                Send Reset Email
              </button>
              <button 
                onClick={() => setShowReset(false)} 
                className="cancel-btn"
              >
                Cancel
              </button>
            </div>
          ) : (
            <button 
              onClick={() => setShowReset(true)} 
              className="forgot-link"
            >
              Forgot Password?
            </button>
          )}
        </div>

        <div className="login-footer">
          <p>New to ClubLink TT?</p>
          <Link to="/register" className="register-link">Register your player here</Link>
        </div>
      </div>
    </div>
  );
}

export default Login;
